import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  setDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  limit,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { calculateFlipAnalysis as calculateFlipAnalysisHelper } from '../utils/calculations';
import { logger } from '../utils/logger';

const UIContext = createContext(null);

const NOTIFICATIONS_PAGE_SIZE = 50;

export function UIProvider({ children }) {
  const { firebaseUser, teamOwnerId } = useAuth();

  // ── Modal & search state ──────────────────────────────────
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showPropertyLookup, setShowPropertyLookup] = useState(false);
  const [newLeadPrefill, setNewLeadPrefill] = useState(null);
  const [showAddNewLead, setShowAddNewLead] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');

  // ── Onboarding ────────────────────────────────────────────
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!firebaseUser?.uid) return;
    const checkOnboarding = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (!data.hasCompletedOnboarding && !data.onboardingDismissed) {
            setShowOnboarding(true);
          }
        } else {
          // New user doc — show onboarding
          setShowOnboarding(true);
        }
      } catch (err) {
        logger.error('Onboarding check failed:', err);
      }
    };
    checkOnboarding();
  }, [firebaseUser?.uid]);

  const saveOnboardingFlag = useCallback(
    async (field) => {
      if (!firebaseUser?.uid) return;
      try {
        await setDoc(doc(db, 'users', firebaseUser.uid), { [field]: true }, { merge: true });
      } catch (err) {
        logger.error('Failed to save onboarding flag:', err);
      }
    },
    [firebaseUser?.uid]
  );

  const completeOnboarding = useCallback(async () => {
    setShowOnboarding(false);
    await saveOnboardingFlag('hasCompletedOnboarding');
  }, [saveOnboardingFlag]);

  const dismissOnboarding = useCallback(async () => {
    setShowOnboarding(false);
    await saveOnboardingFlag('onboardingDismissed');
  }, [saveOnboardingFlag]);

  // ── Notifications ─────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const personalNotifRef = useRef([]);
  const teamNotifRef = useRef([]);

  // Helper: merge personal + team refs into a single sorted array
  const mergeAndSet = useCallback(() => {
    const merged = [...personalNotifRef.current, ...teamNotifRef.current];
    merged.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setNotifications(merged);
  }, []);

  useEffect(() => {
    if (!firebaseUser) return;

    // Reset refs when listeners re-attach
    personalNotifRef.current = [];
    teamNotifRef.current = [];

    // Personal notifications (always)
    const personalQ = query(
      collection(db, 'notifications'),
      where('userId', '==', firebaseUser.uid),
      orderBy('createdAt', 'desc'),
      limit(NOTIFICATIONS_PAGE_SIZE)
    );

    const unsubs = [];

    unsubs.push(
      onSnapshot(
        personalQ,
        (snapshot) => {
          personalNotifRef.current = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            _source: 'personal',
          }));
          mergeAndSet();
        },
        (error) => {
          logger.error('Error loading personal notifications:', error);
        }
      )
    );

    // Team notifications
    if (teamOwnerId && teamOwnerId !== firebaseUser.uid) {
      const teamQ = query(
        collection(db, 'notifications'),
        where('userId', '==', teamOwnerId),
        orderBy('createdAt', 'desc'),
        limit(NOTIFICATIONS_PAGE_SIZE)
      );

      unsubs.push(
        onSnapshot(
          teamQ,
          (snapshot) => {
            teamNotifRef.current = snapshot.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _source: 'team',
            }));
            mergeAndSet();
          },
          (error) => {
            logger.error('Error loading team notifications:', error);
          }
        )
      );
    }

    return () => unsubs.forEach((u) => u());
  }, [firebaseUser, teamOwnerId, mergeAndSet]);

  const addNotification = async (notificationData) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        ...notificationData,
        userId: firebaseUser.uid,
        createdBy: firebaseUser.uid,
        createdByName: firebaseUser.displayName || 'Team Member',
        read: false,
        createdAt: serverTimestamp(),
      });

      if (teamOwnerId && teamOwnerId !== firebaseUser.uid && notificationData.teamBroadcast) {
        await addDoc(collection(db, 'notifications'), {
          ...notificationData,
          userId: teamOwnerId,
          createdBy: firebaseUser.uid,
          createdByName: firebaseUser.displayName || 'Team Member',
          read: false,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      logger.error('Error adding notification:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { read: true });
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const unread = notifications.filter((n) => !n.read);
      await Promise.all(
        unread.map((n) => updateDoc(doc(db, 'notifications', n.id), { read: true }))
      );
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      logger.error('Error deleting notification:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await Promise.all(notifications.map((n) => deleteDoc(doc(db, 'notifications', n.id))));
    } catch (error) {
      logger.error('Error clearing notifications:', error);
    }
  };

  // ── Flip Analysis — persisted to localStorage so inputs survive page refresh ──
  const FLIP_ANALYSIS_DEFAULTS = {
    purchasePrice: '',
    arv: '',
    rehabCost: '',
    sqft: '',
    buyingCosts: 2,
    buyingCostsMode: 'percent',
    holdingMonths: 4,
    holdingCostPerMonth: 1500,
    sellingCosts: 8,
    sellingCostsMode: 'percent',
    financingType: 'cash',
    loanAmount: 80,
    interestRate: 12,
    points: 2,
    targetProfitMargin: 10,
    // BRRRR fields
    analysisMode: 'flip',
    monthlyRent: '',
    propertyTaxMonthly: '',
    insuranceMonthly: '',
    vacancyRate: 8,
    managementRate: 10,
    maintenanceMonthly: '',
    refiLtv: 75,
    refiInterestRate: 7,
    refiLoanTerm: 30,
    annualAppreciation: 3,
    annualRentIncrease: 2,
  };

  const [flipAnalysis, setFlipAnalysisState] = useState(() => {
    try {
      const saved = localStorage.getItem('flipup_flip_analysis');
      return saved ? { ...FLIP_ANALYSIS_DEFAULTS, ...JSON.parse(saved) } : FLIP_ANALYSIS_DEFAULTS;
    } catch {
      return FLIP_ANALYSIS_DEFAULTS;
    }
  });

  // Persist flip analysis inputs whenever they change
  const setFlipAnalysis = useCallback((updater) => {
    setFlipAnalysisState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        localStorage.setItem('flipup_flip_analysis', JSON.stringify(next));
      } catch {
        /* ignore quota errors */
      }
      return next;
    });
  }, []);

  const calculateFlipAnalysis = () => {
    return calculateFlipAnalysisHelper(flipAnalysis);
  };

  // ── Context value ─────────────────────────────────────────
  const value = useMemo(
    () => ({
      showNewProjectModal,
      setShowNewProjectModal,
      showNewLeadModal,
      setShowNewLeadModal,
      showPropertyLookup,
      setShowPropertyLookup,
      showAddNewLead,
      setShowAddNewLead,
      newLeadPrefill,
      setNewLeadPrefill,
      globalSearchQuery,
      setGlobalSearchQuery,
      notifications,
      addNotification,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      deleteNotification,
      clearAllNotifications,
      flipAnalysis,
      setFlipAnalysis,
      calculateFlipAnalysis,
      showOnboarding,
      completeOnboarding,
      dismissOnboarding,
      // Notification action functions (markNotificationAsRead etc.) are stable useCallback refs — intentionally omitted
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [
      showNewProjectModal,
      showNewLeadModal,
      showPropertyLookup,
      showAddNewLead,
      newLeadPrefill,
      globalSearchQuery,
      notifications,
      flipAnalysis,
      showOnboarding,
    ]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within UIProvider');
  return context;
}
